(() => {
  'use strict';

  const STORAGE_KEY = 'demiz_wishlist_v1';
  const CLOUD_SEEDED_KEY = 'demiz_wishlist_cloud_seeded_v1';
  const CLOUD_ENDPOINT = '/apps/demiz-favorites';
  const PRODUCT_PATH = '/products/';

  let cloudLoggedIn = false;
  let cloudReady = false;
  let cloudDigest = null;
  let cloudPushTimer = null;
  let localRevision = 0;

  const heartSVG = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"/>
    </svg>`;

  const safeParse = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  const normalizeProductKey = (value) => {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      const productIndex = url.pathname.indexOf(PRODUCT_PATH);
      const path = productIndex >= 0 ? url.pathname.slice(productIndex) : url.pathname;
      return path.replace(/\/$/, '');
    } catch (error) {
      return String(value).split('?')[0].replace(/\/$/, '');
    }
  };

  const sanitizeItem = (item) => {
    const key = normalizeProductKey(item?.key || item?.url);
    const url = normalizeProductKey(item?.url || item?.key);

    return {
      key,
      url: url || key,
      title: String(item?.title || 'מוצר של דמיז').trim(),
      image: String(item?.image || '').trim(),
      price: String(item?.price || '').trim()
    };
  };

  const sanitizeWishlist = (items) => {
    if (!Array.isArray(items)) return [];

    const result = [];
    const seen = new Set();

    items.forEach((rawItem) => {
      const item = sanitizeItem(rawItem);
      if (!item.key || seen.has(item.key)) return;
      seen.add(item.key);
      result.push(item);
    });

    return result;
  };

  const getWishlist = () => {
    const data = safeParse(localStorage.getItem(STORAGE_KEY), []);
    return sanitizeWishlist(data);
  };

  const wishlistSignature = (items) =>
    sanitizeWishlist(items)
      .map((item) => `${item.key}|${item.title}|${item.image}|${item.price}`)
      .join('\n');

  const wishlistsEqual = (a, b) =>
    wishlistSignature(a) === wishlistSignature(b);

  const mergeWishlists = (...lists) => {
    const result = [];
    const seen = new Set();

    lists.forEach((list) => {
      sanitizeWishlist(list).forEach((item) => {
        if (seen.has(item.key)) return;
        seen.add(item.key);
        result.push(item);
      });
    });

    return result;
  };

  const setWishlist = (items, options = {}) => {
    const normalized = sanitizeWishlist(items);
    const shouldSync = options.sync !== false;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      console.warn('[DEMIZ Wishlist] Could not save wishlist.', error);
    }

    localRevision += 1;

    document.dispatchEvent(new CustomEvent('demiz:wishlist-change', {
      detail: { items: normalized }
    }));

    if (shouldSync) queueCloudPush();
  };

  const isSaved = (key) => {
    const normalized = normalizeProductKey(key);
    return getWishlist().some((item) => item.key === normalized);
  };

  const addItem = (rawItem) => {
    const item = sanitizeItem(rawItem);
    if (!item.key) return;

    const items = getWishlist().filter((existing) => existing.key !== item.key);
    items.unshift(item);
    setWishlist(items);
    showToast('נשמר במועדפים ♡');
  };

  const removeItem = (key, showMessage = true) => {
    const normalized = normalizeProductKey(key);
    const items = getWishlist().filter((item) => item.key !== normalized);
    setWishlist(items);

    if (showMessage) showToast('הוסר מהמועדפים');
  };

  const toggleItem = (item) => {
    const key = normalizeProductKey(item.key || item.url);
    if (!key) return;

    if (isSaved(key)) {
      removeItem(key);
    } else {
      addItem(item);
    }
  };

  const cleanText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const getCardData = (wrapper) => {
    if (!wrapper) return null;

    const productLink = wrapper.querySelector(
      '.card__heading a[href*="/products/"], a.full-unstyled-link[href*="/products/"]'
    );

    if (!productLink) return null;

    const image = wrapper.querySelector('.card__media img, .media img');
    const salePrice = wrapper.querySelector('.price__sale .price-item--sale');
    const regularPrice = wrapper.querySelector(
      '.price__regular .price-item--regular, .price > .price__container .price-item--regular'
    );

    const url = productLink.getAttribute('href') || '';

    return sanitizeItem({
      key: url,
      url,
      title: cleanText(productLink.textContent),
      image: image?.currentSrc || image?.getAttribute('src') || '',
      price: cleanText(salePrice?.textContent || regularPrice?.textContent || '')
    });
  };

  const getButtonData = (button) => {
    if (!button) return null;

    const cardWrapper = button.closest('.card-wrapper');
    if (cardWrapper) return getCardData(cardWrapper);

    const productStudio = button.closest('[id^="DemizProductStudio-"]');
    const currentPrice = productStudio?.querySelector('[data-price]');

    return sanitizeItem({
      key: button.dataset.productKey || button.dataset.productUrl,
      url: button.dataset.productUrl || button.dataset.productKey,
      title: button.dataset.productTitle,
      image: button.dataset.productImage,
      price: cleanText(currentPrice?.textContent || button.dataset.productPrice || '')
    });
  };

  const updateHeaderCount = () => {
    const count = getWishlist().length;

    document.querySelectorAll('[data-demiz-wishlist-count]').forEach((bubble) => {
      bubble.textContent = count > 99 ? '99+' : String(count);
      bubble.hidden = count === 0;
    });
  };

  const updateButtons = () => {
    document.querySelectorAll('[data-demiz-wishlist-button]').forEach((button) => {
      const data = getButtonData(button);
      if (!data?.key) return;

      const saved = isSaved(data.key);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');

      const label = button.querySelector('[data-demiz-wishlist-label]');
      if (label) {
        label.textContent = saved ? 'נשמר במועדפים' : 'שמירה למועדפים';
      }

      const ariaTitle = data.title ? `: ${data.title}` : '';
      button.setAttribute(
        'aria-label',
        `${saved ? 'הסרה מהמועדפים' : 'שמירה למועדפים'}${ariaTitle}`
      );
      button.setAttribute('title', saved ? 'הסרה מהמועדפים' : 'שמירה למועדפים');
    });
  };

  const createCardButton = (item) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'demiz-wishlist-card-button';
    button.setAttribute('data-demiz-wishlist-button', '');
    button.dataset.productKey = item.key;
    button.dataset.productUrl = item.url;
    button.dataset.productTitle = item.title;
    button.dataset.productImage = item.image;
    button.dataset.productPrice = item.price;
    button.innerHTML = heartSVG;
    return button;
  };

  const injectCardButtons = () => {
    document.querySelectorAll('.card-wrapper').forEach((wrapper) => {
      if (wrapper.dataset.demizWishlistReady === 'true') return;

      const cardInner = wrapper.querySelector('.card__inner');
      const item = getCardData(wrapper);

      if (!cardInner || !item?.key) return;

      cardInner.appendChild(createCardButton(item));
      wrapper.dataset.demizWishlistReady = 'true';
    });

    updateButtons();
  };

  const ensureToast = () => {
    let toast = document.querySelector('[data-demiz-wishlist-toast]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'demiz-wishlist-toast';
    toast.setAttribute('data-demiz-wishlist-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.hidden = true;
    document.body.appendChild(toast);
    return toast;
  };

  let toastTimer;
  const showToast = (message) => {
    const toast = ensureToast();
    toast.textContent = message;
    toast.hidden = false;

    requestAnimationFrame(() => toast.classList.add('is-visible'));

    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => {
        toast.hidden = true;
      }, 180);
    }, 1900);
  };

  const createFavoritesCard = (item) => {
    const card = document.createElement('article');
    card.className = 'demiz-favorites-card';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'demiz-favorites-card__media';
    mediaLink.href = item.url;
    mediaLink.setAttribute('aria-label', item.title);

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      mediaLink.appendChild(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'demiz-favorites-card__placeholder';
      placeholder.textContent = 'DEMIZ.FILE';
      mediaLink.appendChild(placeholder);
    }

    const content = document.createElement('div');
    content.className = 'demiz-favorites-card__content';

    const title = document.createElement('a');
    title.className = 'demiz-favorites-card__title';
    title.href = item.url;
    title.textContent = item.title;

    const price = document.createElement('div');
    price.className = 'demiz-favorites-card__price';
    price.textContent = item.price || '';
    if (!item.price) price.hidden = true;

    const actions = document.createElement('div');
    actions.className = 'demiz-favorites-card__actions';

    const visit = document.createElement('a');
    visit.className = 'demiz-favorites-card__visit';
    visit.href = item.url;
    visit.textContent = 'למוצר';

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'demiz-favorites-card__remove';
    remove.setAttribute('data-demiz-wishlist-remove', '');
    remove.dataset.productKey = item.key;
    remove.innerHTML = `<span aria-hidden="true">×</span><span>הסרה</span>`;
    remove.setAttribute('aria-label', `הסרה מהמועדפים: ${item.title}`);

    actions.append(visit, remove);
    content.append(title, price, actions);
    card.append(mediaLink, content);

    return card;
  };

  const renderFavoritesPage = () => {
    const page = document.querySelector('[data-demiz-wishlist-page]');
    if (!page) return;

    const items = getWishlist();
    const grid = page.querySelector('[data-demiz-wishlist-items]');
    const empty = page.querySelector('[data-demiz-wishlist-empty]');
    const count = page.querySelector('[data-demiz-wishlist-page-count]');

    if (count) count.textContent = String(items.length);
    if (!grid || !empty) return;

    grid.replaceChildren();

    if (!items.length) {
      grid.hidden = true;
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    grid.hidden = false;

    items.forEach((item) => {
      grid.appendChild(createFavoritesCard(item));
    });
  };

  /* =========================================================
     CLOUD SYNC
     ========================================================= */

  const cloudRequest = async (method, body) => {
    const options = {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    };

    if (body !== undefined) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    const response = await fetch(CLOUD_ENDPOINT, options);

    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      throw new Error(`Cloud sync returned ${response.status} without JSON.`);
    }

    return { response, data };
  };

  const pushCloudWishlist = async (items, retry = true) => {
    if (!cloudReady || !cloudLoggedIn) return;

    try {
      const { response, data } = await cloudRequest('POST', {
        favorites: sanitizeWishlist(items),
        compareDigest: cloudDigest
      });

      if (response.status === 409 && retry) {
        cloudDigest = data?.compareDigest ?? null;
        await pushCloudWishlist(getWishlist(), false);
        return;
      }

      if (!response.ok || !data?.ok) {
        console.warn('[DEMIZ Wishlist] Cloud save failed.', response.status, data);
        return;
      }

      cloudDigest = data.compareDigest ?? null;
      localStorage.setItem(CLOUD_SEEDED_KEY, '1');
    } catch (error) {
      console.warn('[DEMIZ Wishlist] Cloud save unavailable. Local favorites are safe.', error);
    }
  };

  function queueCloudPush() {
    if (!cloudReady || !cloudLoggedIn) return;

    window.clearTimeout(cloudPushTimer);
    cloudPushTimer = window.setTimeout(() => {
      pushCloudWishlist(getWishlist());
    }, 350);
  }

  const initCloudSync = async () => {
    const revisionAtStart = localRevision;

    try {
      const { response, data } = await cloudRequest('GET');

      if (!response.ok || !data?.ok) {
        console.warn('[DEMIZ Wishlist] Cloud sync unavailable. Using local favorites.', data);
        return;
      }

      if (!data.loggedIn) {
        cloudLoggedIn = false;
        cloudReady = false;
        cloudDigest = null;
        localStorage.removeItem(CLOUD_SEEDED_KEY);
        return;
      }

      cloudLoggedIn = true;
      cloudDigest = data.compareDigest ?? null;

      const localItems = getWishlist();
      const remoteItems = sanitizeWishlist(data.favorites || []);
      const wasSeeded = localStorage.getItem(CLOUD_SEEDED_KEY) === '1';
      const changedWhileLoading = localRevision !== revisionAtStart;

      let nextItems;
      let shouldPush = false;

      if (!wasSeeded || changedWhileLoading) {
        // First cloud connection on this browser: preserve everything the
        // customer already saved locally and merge it into the account.
        nextItems = mergeWishlists(localItems, remoteItems);
        shouldPush = !wishlistsEqual(nextItems, remoteItems);
      } else {
        // Once this browser has synced, Shopify becomes the source of truth
        // when the page loads. Local clicks still sync back immediately.
        nextItems = remoteItems;
      }

      if (!wishlistsEqual(localItems, nextItems)) {
        setWishlist(nextItems, { sync: false });
      }

      cloudReady = true;

      if (shouldPush) {
        await pushCloudWishlist(nextItems);
      } else {
        localStorage.setItem(CLOUD_SEEDED_KEY, '1');
      }

      refresh();
    } catch (error) {
      console.warn('[DEMIZ Wishlist] Cloud sync unavailable. Using local favorites.', error);
    }
  };

  const refresh = () => {
    injectCardButtons();
    updateHeaderCount();
    updateButtons();
    renderFavoritesPage();
  };

  document.addEventListener('click', (event) => {
    const wishlistButton = event.target.closest('[data-demiz-wishlist-button]');
    if (wishlistButton) {
      event.preventDefault();
      event.stopPropagation();

      const item = getButtonData(wishlistButton);
      if (item?.key) toggleItem(item);
      return;
    }

    const removeButton = event.target.closest('[data-demiz-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      removeItem(removeButton.dataset.productKey);
    }
  });

  document.addEventListener('demiz:wishlist-change', refresh);
  document.addEventListener('shopify:section:load', refresh);
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      refresh();
      queueCloudPush();
    }
  });
  window.addEventListener('pageshow', refresh);

  let mutationQueued = false;
  const observer = new MutationObserver(() => {
    if (mutationQueued) return;
    mutationQueued = true;

    requestAnimationFrame(() => {
      mutationQueued = false;
      injectCardButtons();
    });
  });

  const init = () => {
    refresh();
    observer.observe(document.body, { childList: true, subtree: true });
    initCloudSync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
